import React, { useEffect, useState, useRef } from 'react';
import { analyticsService } from '../../../../services/apiService';

const AdminLocationDropdown = ({ value = '', onChange = () => {}, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [area, setArea] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [entries, setEntries] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    // If parent passes an object (custom), show custom mode values
    if (value && typeof value === 'object') {
      
      setArea(value.area || '');
      setCity(value.city || '');
      setStateVal(value.state || '');
      setCountry(value.country || '');
    } else {
      
      setArea('');
      setCity('');
      setStateVal('');
      setCountry('');
    }
  }, [value]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await analyticsService.getLocations();
        if (!mounted) return;
        // Normalize: backend returns array of { country, state, city, area }; support legacy { value, label }
        const raw = Array.isArray(res) ? res : [];
        const e = raw.map(item => {
          if (!item || typeof item !== 'object') return null;
          if (item.country != null || item.state != null || item.city != null || item.area != null) {
            return {
              country: (item.country ?? '').toString().trim(),
              state: (item.state ?? '').toString().trim(),
              city: (item.city ?? '').toString().trim(),
              area: (item.area ?? '').toString().trim()
            };
          }
          if (item.value != null || item.label != null) {
            const label = (item.label ?? item.value ?? '').toString().trim();
            if (label) return { country: label, state: '', city: '', area: '' };
          }
          return null;
        }).filter(Boolean);
        setEntries(e);
        const countrySet = new Set();
        e.forEach(item => {
          if (item.country) countrySet.add(item.country);
        });
        if (value && typeof value === 'object' && value.country) {
          countrySet.add(value.country);
        }
        setCountries(Array.from(countrySet).filter(Boolean).sort());
      } catch (err) {
        console.error('Failed to load locations', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  // no preset options - simple custom fields only

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // no preset handler - only applyCustom is used

  const applyCustom = () => {
    setError('');
    // allow partial entries; if all empty, clear selection
    if (!area.trim() && !city.trim() && !stateVal.trim() && !country.trim()) {
      onChange && onChange('');
      setOpen(false);
      return;
    }
    const obj = {
      ...(area.trim() ? { area: area.trim() } : {}),
      ...(city.trim() ? { city: city.trim() } : {}),
      ...(stateVal.trim() ? { state: stateVal.trim() } : {}),
      ...(country.trim() ? { country: country.trim() } : {})
    };
    onChange && onChange(obj);
    setOpen(false);
  };

  // Compute dependent lists: States for selected Country; Cities for Country+State; Areas for Country+State+City
  useEffect(() => {
    const sSet = new Set();
    const cSet = new Set();
    const aSet = new Set();
    entries.forEach(it => {
      if (!it) return;
      const matchCountry = !country || it.country === country;
      const matchState = !stateVal || it.state === stateVal;
      const matchCity = !city || it.city === city;
      if (matchCountry && it.state) sSet.add(it.state);
      if (matchCountry && matchState && it.city) cSet.add(it.city);
      if (matchCountry && matchState && matchCity && it.area) aSet.add(it.area);
    });
    setStates(Array.from(sSet).filter(Boolean).sort());
    setCities(Array.from(cSet).filter(Boolean).sort());
    setAreas(Array.from(aSet).filter(Boolean).sort());
  }, [entries, country, stateVal, city]);

  // When dropdown opens, try to align current selection with fetched entries.
  // This makes sure dependent lists (states/cities/areas) are filtered correctly
  // even if entries arrived after the parent passed the `value` prop.
  useEffect(() => {
    if (!open) return;
    if (!entries || entries.length === 0) return;
    if (value && typeof value === 'object') {
      const vCountry = (value.country || '').trim();
      const vState = (value.state || '').trim();
      const vCity = (value.city || '').trim();
      const vArea = (value.area || '').trim();

      // Try to find the best matching entry from fetched data (case-insensitive)
      const match = entries.find(it => {
        if (!it) return false;
        const ic = (it.country || '').trim().toLowerCase();
        const is = (it.state || '').trim().toLowerCase();
        const iy = (it.city || '').trim().toLowerCase();
        const ia = (it.area || '').trim().toLowerCase();
        if (vCountry && ic !== vCountry.toLowerCase()) return false;
        if (vState && is !== vState.toLowerCase()) return false;
        if (vCity && iy !== vCity.toLowerCase()) return false;
        if (vArea && ia !== vArea.toLowerCase()) return false;
        return true;
      });

      if (match) {
        setCountry(match.country || vCountry);
        setStateVal(match.state || vState);
        setCity(match.city || vCity);
        setArea(match.area || vArea);
      } else {
        // Fallback to whatever parent provided (may be custom)
        setCountry(vCountry);
        setStateVal(vState);
        setCity(vCity);
        setArea(vArea);
      }
    }
  }, [open, entries, value]);

  const selectedLabel = () => {
    const parts = [];
    if (area && String(area).trim()) parts.push(area.trim());
    if (city && String(city).trim()) parts.push(city.trim());
    if (stateVal && String(stateVal).trim()) parts.push(stateVal.trim());
    if (country && String(country).trim()) parts.push(country.trim());
    if (parts.length > 0) return parts.join(' — ');
    return 'Location';
  };

  return (
    <div ref={containerRef} className={`relative inline-block text-left ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-4 py-2 border rounded-full bg-white text-sm font-semibold shadow-sm hover:shadow-md"
        aria-haspopup="true"
        aria-expanded={open}
        title="Location"
      >
        <span className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c1.657 0 3-1.343 3-3S13.657 5 12 5 9 6.343 9 8s1.343 3 3 3z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21s8-4.5 8-10a8 8 0 10-16 0c0 5.5 8 10 8 10z"/></svg>
          <span>{selectedLabel()}</span>
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" /></svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 z-50 p-4">
          <div className="space-y-3">
            <div className="space-y-2">
              <div>
                <label className="text-[11px] font-bold text-gray-500">Country</label>
                <select value={country} onChange={(e) => { setCountry(e.target.value); setStateVal(''); setCity(''); setArea(''); }} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                  <option value="">Any</option>
                  {loading ? <option value="">Loading...</option> : countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-500">State</label>
                <select value={stateVal} onChange={(e) => { setStateVal(e.target.value); setCity(''); setArea(''); }} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                  <option value="">Any</option>
                  {states.filter(s => !country || s).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-500">City</label>
                <select value={city} onChange={(e) => { setCity(e.target.value); setArea(''); }} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                  <option value="">Any</option>
                  {cities.filter(c => !stateVal || c).map(ci => <option key={ci} value={ci}>{ci}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-500">Area</label>
                <select value={area} onChange={(e) => setArea(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                  <option value="">Any</option>
                  {areas.filter(a => !city || a).map(ar => <option key={ar} value={ar}>{ar}</option>)}
                </select>
              </div>
              <div className="pt-2">
                <button onClick={applyCustom} className="w-full py-2 bg-red-700 text-white rounded-lg font-bold">Apply</button>
              </div>
              {error && <div className="text-red-500 text-xs">{error}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLocationDropdown;

